import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { DrawerModule } from 'primeng/drawer';
import { Select } from 'primeng/select';

import { ClienteService } from '@shared/api/services/cliente.service';
import { CategoriaService } from '@shared/api/services/categoria.service';
import { EfetivoService } from '@shared/api/services/efetivo.service';
import { EquipamentoService } from '@shared/api/services/equipamento.service';
import { PostoService } from '@shared/api/services/posto.service';
import { RondaService } from '@shared/api/services/ronda.service';
import { EscalaService } from '@shared/api/services/escala.service';
import { FaltaService } from '@shared/api/services/falta.service';
import { OcorrenciaService } from '@shared/api/services/ocorrencia.service';

import { DocumentFaltaService } from '@shared/api/services/document-falta.service';
import { DocumentEfetivoService } from '@shared/api/services/document-efetivo.service';
import { DocumentEscalaService } from '@shared/api/services/document-escala.service';
import { DocumentcorrenciaService } from '@shared/api/services/document-ocorrencia.service';
import { DocumentRondaService } from '@shared/api/services/document-ronda.service';
import { DocumentEquipamentosService } from '@shared/api/services/document-equipamento.service';
import { DocumentPostoService } from '@shared/api/services/document-posto.service';

import {
  ContratoEfetivoService,
  MunicipioService,
  ProvinciaService,
  TurnoService,
} from '@core/services/domain-services';
import { TenantConfigurationService } from '@core/services/tenant-configuration.service';

import { ReportGeneralFiltersComponent } from './components/report-general-filters/report-general-filters.component';
import { ReportCatalogComponent } from './components/report-catalog/report-catalog.component';
import { ReportPageHeaderComponent } from './components/report-page-header/report-page-header.component';
import { ReportPreviewComponent } from './components/report-preview/report-preview.component';
import { RelatorioSelectOption } from './relatorios.models';
import {
  OutputFormat,
  PreviewColumn,
  PreviewRow,
  ReportDefinition,
  ReportId,
  FaltaDocumentoParams,
  OcorrenciaDocumentoParams,
  RondaDocumentParams,
  EfectivoDocumentoParams,
  EscalaDocumentoParams,
  EquipamentoDocumentoParams,
  PostoDocumentoParams,
} from '@core/models';

@Component({
  selector: 'app-relatorios',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Select,
    DrawerModule,
    ReportPageHeaderComponent,
    ReportGeneralFiltersComponent,
    ReportCatalogComponent,
    ReportPreviewComponent,
  ],
  templateUrl: './relatorios.component.html',
  styleUrl: './relatorios.component.scss',
})
export class RelatoriosComponent implements OnInit {

  constructor(
    private readonly efetivoService: EfetivoService,
    private readonly postoService: PostoService,
    private readonly equipamentoService: EquipamentoService,
    private readonly clienteService: ClienteService,
    private readonly categoriaService: CategoriaService,
    private readonly contratoEfetivoService: ContratoEfetivoService,
    private readonly municipioService: MunicipioService,
    private readonly provinciaService: ProvinciaService,
    private readonly turnoService: TurnoService,
    private readonly faltaService: FaltaService,
    private readonly rondaService: RondaService,
    private readonly escalaService: EscalaService,
    private readonly ocorrenciaService: OcorrenciaService,

    readonly tenantConfigurationService: TenantConfigurationService,

    private readonly documentFaltaService: DocumentFaltaService,
    private readonly documentEfectivoService: DocumentEfetivoService,
    private readonly documentEscalaService: DocumentEscalaService,
    private readonly documentcorrenciaService: DocumentcorrenciaService,
    private readonly documentRondaService: DocumentRondaService,
    private readonly documentEquipamentoService: DocumentEquipamentosService,
    private readonly documentPostoService: DocumentPostoService,
  ) {}

  // INIT
  ngOnInit(): void {
    this.carregarConfiguracaoRelatorios();
    this.carregarPostos();
    this.carregarProvincias();
    this.carregarClientes();
    this.carregarEfetivos();
    this.carregarEquipamentos();
    this.carregarCategorias();
    this.carregarAlocacoesEfetivo();
    this.carregarFaltas();
    this.carregarRondas();
    this.carregarEscalas();
    this.carregarTurnos();
    this.carregarOcorrencias();
  }

  carregarConfiguracaoRelatorios(): void {
    this.carregandoCatalogo = true;
    this.falhaAoCarregarCatalogo = false;

    this.tenantConfigurationService
      .load()
      .pipe(finalize(() => (this.carregandoCatalogo = false)))
      .subscribe({
        error: () => (this.falhaAoCarregarCatalogo = true),
      });
  }

  private carregarProvincias(): void {
    this.carregandoProvincias = true;
    this.provinciaService.getAll({ pageNumber: 1, pageSize: 1000 }).subscribe({
      next: (response) => {
        const provincias = this.extrairDados(response);
        this.provincia = [
          { label: 'Todas as Províncias', value: null },
          ...provincias
            .filter((provincia: any) => provincia.id != null)
            .map((provincia: any) => ({
              label: provincia.designacao ?? provincia.nome ?? provincia.name,
              value: Number(provincia.id),
            }))
            .filter((option: RelatorioSelectOption) => !!option.label),
        ];
        this.carregandoProvincias = false;
      },
      error: (error: unknown) => {
        console.error('Erro ao carregar províncias:', error);
        this.provincia = [{ label: 'Todas as Províncias', value: null }];
        this.carregandoProvincias = false;
      },
    });
  }

  onProvinciaChange(idProvincia: number | null): void {
    this.ProvinciaId = idProvincia;
    this.MunicipioId = null;
    this.municipio = [{ label: 'Selecione uma província', value: null }];

    if (idProvincia == null) {
      this.carregandoMunicipios = false;
      return;
    }

    this.carregandoMunicipios = true;
    this.municipioService.getByProvincia(idProvincia).subscribe({
      next: (response) => {
        const municipios = this.extrairDados(response);
        this.municipio = [
          { label: 'Todos os Municípios', value: null },
          ...municipios
            .filter((municipio: any) => municipio.id != null)
            .map((municipio: any) => ({
              label: municipio.designacao ?? municipio.nome ?? municipio.name,
              value: Number(municipio.id),
            }))
            .filter((option: RelatorioSelectOption) => !!option.label),
        ];
        this.carregandoMunicipios = false;
      },
      error: (error: unknown) => {
        console.error('Erro ao carregar municípios:', error);
        this.municipio = [{ label: 'Todos os Municípios', value: null }];
        this.carregandoMunicipios = false;
      },
    });
  }

  // PERÍODOS
  periodos: RelatorioSelectOption[] = [
    { label: 'Este Mês', value: 1 },
    { label: 'Hoje', value: 2 },
    { label: 'Esta Semana', value: 3 },
    { label: 'Último trimestre', value: 4 },
    { label: 'Personalizado', value: 5 },
  ];

  // FORMATOS
  formatoSelecionado: OutputFormat = 'pdf';
  formatos: RelatorioSelectOption[] = [
    { label: 'DOCX', value: 'docx' },
    { label: 'PDF', value: 'pdf' },
    { label: 'XLSX', value: 'xlsx' },
  ];

  relatorioSelecionado: ReportId = 'postos';
  IsActive: boolean | null = null;

  periodoSelecionado = 1;

  dataInicioDe = '';
  dataInicioAte = '';
  dataAquisicao = '';
  dataAdmissao = '';

  // INTERFACE
  drawerVisible = false;
  previewVisible = false;
  gerandoDocumento = false;

  previewTitulo = '';
  previewDescricao = '';
  previewIcone = '';

  previewColumns: PreviewColumn[] = [];
  previewRows: PreviewRow[] = [];

  // LOADING
  carregandoCatalogo = true;
  falhaAoCarregarCatalogo = false;
  carregandoPostos = false;
  carregandoClientes = false;
  carregandoFaltas = false;
  carregandoEfetivos = false;
  carregandoEquipamentos = false;
  carregandoAlocacoes = false;
  carregandoRondas = false;
  carregandoEscalas = false;
  carregandoTurnos = false;
  carregandoOcorrencias = false;

  // EQUIPAMENTOS

  TipoEquipamentoId: number | null = null;
  tiposEquipamentos: RelatorioSelectOption[] = [
    { label: 'Todos os Equipamentos', value: null },
  ];

  // POSTOS
  TipoPostoId: number | null = null;
  tipoPosto: RelatorioSelectOption[] = [
    { label: 'TODOS OS TIPOS', value: null },
    { label: 'POSTO FIXO', value: '1' },
    { label: 'POSTO MÓVEL (RONDA)', value: '2' },
    { label: 'Posto DE CONTROLO DE ACESSOS', value: '3' },
    { label: 'POSTO DE PROTEÇÃO PESSOAL', value: '5' },
    { label: 'POSTO INDÚSTRIAL OU PATRIMONIAL', value: 9 },
  ];

  postoSelecionado: string | number | null = null;
  postos: RelatorioSelectOption[] = [
    { label: 'Todos os postos', value: null },
  ];
  ProvinciaId: number | null = null;
  provincia: RelatorioSelectOption[] = [
    { label: 'Todas as Províncias', value: null },
  ];
  carregandoProvincias = false;
  MunicipioId: number | null = null;
  municipio: RelatorioSelectOption[] = [
    { label: 'Selecione uma província', value: null },
  ];
  carregandoMunicipios = false;

  // CLIENTES
  clientes: RelatorioSelectOption[] = [
    { label: 'Todos os clientes', value: null },
  ];
  clienteSelecionado: string | number | null = null;

  // OCORRÊNCIAS
  efetivoSelecionado: string | number | null = null;

  rondaSelecionada: string | number | null = null;
  tiposRonda: RelatorioSelectOption[] = [
    { label: 'Todas as Rondas', value: null },
  ];

  criticidadeSelecionada: string | number | null = null;
  criticidades: RelatorioSelectOption[] = [
    { label: 'Todas as Criticidades', value: null },
  ];

  tipoOcorrenciaSelecionado: string | number | null = null;
  tiposOcorrencia: RelatorioSelectOption[] = [
    { label: 'Todos os Tipos', value: null },
  ];

  // RONDAS
  SomenteEmcurso = false;

  // ESCALAS E TURNOS
  turnoSelecionado: string | number | null = null;
  turnos: RelatorioSelectOption[] = [
    { label: 'Todos os turnos', value: null },
  ];

  escalaSelecionada: string | number | null = null;
  escalas: RelatorioSelectOption[] = [
    { label: 'Todas as escalas', value: null },
  ];
  estadosEscala: RelatorioSelectOption[] = [
    { label: 'Todos os estados', value: null },
    { label: 'AGENDADA', value: '0' },
    { label: 'DECORRENDO', value: '1' },
    { label: 'CONCLUÍDA', value: '2' },
    { label: 'INTERROMPIDA', value: '3' },
    { label: 'CANCELADA', value: '4' },
  ];

  // EFETIVOS
  efetivosOpcoes: RelatorioSelectOption[] = [
    { label: 'Todos os efetivos', value: null },
  ];
  estadoSelecionado: string | number | null = null;

  estadosEfetivo: RelatorioSelectOption[] = [
    { label: 'Todos os Estados', value: null },
    { label: 'ACTIVO', value: '0' },
    { label: 'INACTIVO', value: '1' },
    { label: 'AFASTADO', value: '2' },
    { label: 'SUSPENSOS', value: '3' },
    { label: 'DE FOLGA', value: '4' },
    { label: 'FÉRIAS', value: '5' },
  ];

  // DADOS DA API
  efetivos: any[] = [];
  equipamentos: any[] = [];
  postosDados: any[] = [];
  faltasDados: any[] = [];
  categorias: any[] = [];
  alocacoesEfetivo: any[] = [];
  rondasDados: any[] = [];
  escalasDados: any[] = [];
  ocorrenciasDados: any[] = [];

  // CATÁLOGO DE RELATÓRIOS
  readonly reportCatalog: ReportDefinition[] = [
    {
      id: 'efetivos',
      title: 'Efetivos',
      description: 'Lista de efetivos por posto, estado e categoria profissional.',
      icon: 'pi pi-users',
      color: 'blue',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
    {
      id: 'faltas',
      title: 'Faltas',
      description: 'Assiduidade dos efetivos no período: presenças, faltas e atrasos.',
      icon: 'pi pi-clock',
      color: 'cyan',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
    {
      id: 'escalas',
      title: 'Escalas',
      description: 'Escalas planeadas por posto, regime e turno.',
      icon: 'pi pi-calendar',
      color: 'purple',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
    {
      id: 'ocorrencias',
      title: 'Ocorrências',
      description: 'Ocorrências registadas por efetivo, posto, criticidade e tipo.',
      icon: 'pi pi-exclamation-triangle',
      color: 'orange',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
    {
      id: 'rondas',
      title: 'Rondas',
      description: 'Cumprimento de rondas: pontos verificados vs. planeados.',
      icon: 'pi pi-map',
      color: 'teal',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
    {
      id: 'financeiro',
      title: 'Financeiro',
      description: 'Faturação e custos operacionais por cliente.',
      icon: 'pi pi-chart-line',
      color: 'green',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: false,
    },
    {
      id: 'postos',
      title: 'Postos',
      description: 'Estado, cobertura e localização dos postos de guarda.',
      icon: 'pi pi-map-marker',
      color: 'pink',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
    {
      id: 'equipamentos',
      title: 'Equipamentos',
      description: 'Inventário e atribuição de equipamentos e armamento.',
      icon: 'pi pi-shield',
      color: 'gray',
      formats: ['DOCX', 'PDF', 'XLSX'],
      available: true,
    },
  ];

  // RELATÓRIOS DISPONÍVEIS
  get reports(): ReportDefinition[] {
    const catalogById = new Map(
      this.reportCatalog.map((report) => [report.id, report]),
    );

    return this.tenantConfigurationService
      .configuration()
      .reportConfigurations.filter((configuration) => configuration.enabled)
      .map((configuration) => {
        const report = catalogById.get(configuration.id);
        return report ? { ...report, title: configuration.name } : null;
      })
      .filter((report): report is ReportDefinition => report !== null);
  }

  // OBTER O RELATÓRIO
  private obterRelatorio(id: ReportId): ReportDefinition {
    return (
      this.reports.find((report) => report.id === id) ??
      this.reportCatalog.find((report) => report.id === id) ??
      this.reportCatalog[0]
    );
  }

  // ABRIR DRAWER
  abrirBtnGerar(id: ReportId): void {
    const report = this.obterRelatorio(id);

    if (!report.available) {
      return;
    }

    this.relatorioSelecionado = id;
    this.drawerVisible = true;
  }

  fecharDrawer(): void {
    this.drawerVisible = false;
  }

  // SELECIONAR FORMATO
  selecionarFormato(formato: string): void {
    switch (formato.toUpperCase()) {
      case 'DOCX':
        this.formatoSelecionado = 'docx';
        break;

      case 'PDF':
        this.formatoSelecionado = 'pdf';
        break;

      case 'XLSX':
        this.formatoSelecionado = 'xlsx';
        break;
    }
  }

  private obterFormatoDocumento(): number {
    switch (this.formatoSelecionado) {
      case 'docx':
        return 1;

      case 'xlsx':
        return 3;

      case 'pdf':
      default:
        return 2;
    }
  }

  // RELATÓRIO - EFETIVOS
  private gerarRelatorioEfetivos(): void {
    const params: EfectivoDocumentoParams = {
      PeriodoEnum: this.periodoSelecionado,
      DataAdmissaoDe: this.dataAdmissao || undefined,
      DataAdmissaoAte: this.dataAdmissao || undefined,
      PostoId: this.obterNumeroOpcional(this.postoSelecionado),
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      EstadoEfetivo: this.obterNumeroOpcional(this.estadoSelecionado),
      formatoDocumento: this.obterFormatoDocumento(),
    };

    this.documentEfectivoService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-efetivos');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de efetivos:', error);
        this.finalizarDownload();
      },
    });
  }

  // RELATÓRIO - FALTAS
  private gerarRelatorioFaltas(): void {
    const params: FaltaDocumentoParams = {
      PeriodoEnum: this.periodoSelecionado,
      PostoId: this.obterNumeroOpcional(this.postoSelecionado),
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      formatoDocumento: this.obterFormatoDocumento(),
    };

    this.documentFaltaService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-faltas');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de faltas:', error);
        this.finalizarDownload();
      },
    });
  }

  // RELATÓRIO - ESCALAS
  private gerarRelatorioEscalas(): void {
    const params: EscalaDocumentoParams = {
      PeriodoEnum: this.periodoSelecionado,
      DataInicioDe: this.dataInicioDe || undefined,
      DataInicioAte: this.dataInicioAte || undefined,
      DataCriacaoDe: this.dataInicioDe || undefined,
      DataCriacaoAte: this.dataInicioAte || undefined,
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      PostoId: this.obterNumeroOpcional(this.postoSelecionado),
      TurnoId: this.obterNumeroOpcional(this.turnoSelecionado),
      Estado: this.obterNumeroOpcional(this.estadoSelecionado),
      EscalaId: this.obterNumeroOpcional(this.escalaSelecionada),
      formatoDocumento: this.obterFormatoDocumento(),
    };

    this.documentEscalaService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-escalas');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de escalas:', error);
        this.finalizarDownload();
      },
    });
  }

  // RELATÓRIO - OCORRÊNCIAS
  private gerarRelatorioOcorrencias(): void {
    const params: OcorrenciaDocumentoParams = {
      PeriodoEnum: this.periodoSelecionado,
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      PostoId: this.obterNumeroOpcional(this.postoSelecionado),
      TurnoId: this.obterNumeroOpcional(this.turnoSelecionado),
      EfetivoId: this.obterNumeroOpcional(this.efetivoSelecionado),
      RondaId: this.obterNumeroOpcional(this.rondaSelecionada),
      Criticidade: this.obterNumeroOpcional(this.criticidadeSelecionada),
      TipoOcorrencia: this.obterNumeroOpcional(this.tipoOcorrenciaSelecionado),
      formatoDocumento: this.obterFormatoDocumento(),
    };

    this.documentcorrenciaService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-ocorrencias');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de ocorrências:', error);
        this.finalizarDownload();
      },
    });
  }

  // RELATÓRIO - RONDAS
  private gerarRelatorioRonda(): void {
    const params: RondaDocumentParams = {
      PeriodoEnum: this.periodoSelecionado,
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      PostoId: this.obterNumeroOpcional(this.postoSelecionado),
      TurnoId: this.obterNumeroOpcional(this.turnoSelecionado),
      EfetivoId: this.obterNumeroOpcional(this.efetivoSelecionado),
      SomenteEmCurso: this.SomenteEmcurso,
    };

    this.documentRondaService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-rondas');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de rondas:', error);
        this.finalizarDownload();
      },
    });
  }

  // RELATÓRIO - EQUIPAMENTOS
  private gerarRelatorioEquipamento(): void {
    const params: EquipamentoDocumentoParams = {
      PeriodoEnum: this.periodoSelecionado,
      DataInicioDe: this.dataAquisicao || undefined,
      DataInicioAte: this.dataAquisicao || undefined,
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      TipoEquipamentoId: this.TipoEquipamentoId ?? undefined,
      IsActive: this.IsActive ?? undefined,
    };

    this.documentEquipamentoService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-equipamentos');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de equipamentos:', error);
        this.finalizarDownload();
      },
    });
  }

  // RELATÓRIO - POSTOS
  private gerarRelatorioPosto(): void {
    const params: PostoDocumentoParams = {
      ClienteId: this.obterNumeroOpcional(this.clienteSelecionado),
      TipoPostoId: this.TipoPostoId ?? undefined,
      ProvinciaId: this.ProvinciaId ?? undefined,
      MunicipioId: this.MunicipioId ?? undefined,
      IsActive: this.IsActive ?? undefined,
    };

    this.documentPostoService.gerarDocumento(params).subscribe({
      next: (blob: Blob) => {
        this.descarregarBlob(blob, 'relatorio-postos');
        this.finalizarDownload();
      },
      error: (error: unknown) => {
        console.error('Erro ao gerar relatório de postos:', error);
        this.finalizarDownload();
      },
    });
  }

  // DOWNLOAD
  baixarRelatorio(): void {
    if (this.gerandoDocumento) {
      return;
    }

    const id = this.relatorioSelecionado;
    this.gerandoDocumento = true;

    switch (id) {
      case 'efetivos':
        this.gerarRelatorioEfetivos();
        break;

      case 'faltas':
        this.gerarRelatorioFaltas();
        break;

      case 'escalas':
        this.gerarRelatorioEscalas();
        break;

      case 'ocorrencias':
        this.gerarRelatorioOcorrencias();
        break;

      case 'rondas':
        this.gerarRelatorioRonda();
        break;

      case 'equipamentos':
        this.gerarRelatorioEquipamento();
        break;

      case 'postos':
        this.gerarRelatorioPosto();
        break;

      default:
        this.finalizarDownload();
        break;
    }
  }

  private finalizarDownload(): void {
    this.gerandoDocumento = false;
    this.drawerVisible = false;
    this.previewVisible = false;
  }

  private descarregarBlob(blob: Blob, nomeBase: string): void {
    if (!blob) {
      return;
    }

    const extensao = this.obterExtensaoFormato();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${this.normalizarNome(nomeBase)}_${this.dataArquivo()}.${extensao}`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private dataArquivo(): string {
    const agora = new Date();
    const ano = agora.getFullYear();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const dia = String(agora.getDate()).padStart(2, '0');

    return `${ano}-${mes}-${dia}`;
  }

  private obterExtensaoFormato(): string {
    switch (this.formatoSelecionado) {
      case 'docx':
        return 'docx';

      case 'xlsx':
        return 'xlsx';

      case 'pdf':
      default:
        return 'pdf';
    }
  }

  // GETTERS
  get previewTotalRegistos(): number {
    return this.previewRows.length;
  }

  get relatorioAtual(): ReportDefinition {
    return this.obterRelatorio(this.relatorioSelecionado);
  }

  get opcoesEstadoAtuais(): RelatorioSelectOption[] {
    return this.relatorioSelecionado === 'escalas'
      ? this.estadosEscala
      : this.estadosEfetivo;
  }

  get rotuloEstadoAtual(): string {
    return this.relatorioSelecionado === 'escalas'
      ? 'Estado da escala'
      : 'Estado do efetivo';
  }

  // CARREGAR POSTOS
  private carregarPostos(): void {
    this.carregandoPostos = true;

    this.postoService
      .list({
        pageNumber: 1,
        pageSize: 1000,
      })
      .subscribe({
        next: (response: unknown) => {
          const dados = this.extrairDados(response);
          this.postosDados = dados;
          this.atualizarOpcoesPostosPorCliente(this.clienteSelecionado);
          this.carregandoPostos = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar postos:', error);
          this.postosDados = [];
          this.postos = [{ label: 'Todos os postos', value: null }];
          this.carregandoPostos = false;
        },
      });
  }

  // ALTERAÇÃO DO CLIENTE
  onClienteChange(clienteId: string | number | null): void {
    this.clienteSelecionado =
      clienteId === '' || clienteId === undefined ? null : clienteId;

    this.postoSelecionado = null;
    this.atualizarOpcoesPostosPorCliente(this.clienteSelecionado);
  }

  // POSTOS DEPENDENTES DO CLIENTE
  private atualizarOpcoesPostosPorCliente(
    clienteId: string | number | null,
  ): void {
    const postosFiltrados =
      clienteId === null || clienteId === undefined || clienteId === ''
        ? this.postosDados
        : this.postosDados.filter((item) => {
            const postoClienteId = this.obterValor(item, [
              'idCliente',
              'IdCliente',
              'clienteId',
              'ClienteId',
              'idClientePosto',
              'IdClientePosto',
              'cliente.id',
              'cliente.Id',
              'cliente.idCliente',
              'cliente.IdCliente',
            ]);

            return this.correspondeId(clienteId, postoClienteId);
          });

    this.postos = [
      { label: 'Todos os postos', value: null },
      ...postosFiltrados
        .map((item) => {
          const id = this.obterIdSeguro(item);
          if (id === null) {
            return null;
          }

          return {
            label: this.obterTexto(
              item,
              [
                'nome',
                'Nome',
                'designacao',
                'Designacao',
                'nomePosto',
                'NomePosto',
              ],
              'Posto sem designação',
            ),
            value: id,
          };
        })
        .filter(
          (item): item is { label: string; value: string | number } =>
            item !== null,
        ),
    ];
  }

  // CARREGAR CLIENTES
  private carregarClientes(): void {
    this.carregandoClientes = true;

    this.clienteService
      .list({
        pageNumber: 1,
        pageSize: 1000000,
      })
      .subscribe({
        next: (response: unknown) => {
          const dados = this.extrairDados(response);

          this.clientes = [
            { label: 'Todos os clientes', value: null },
            ...dados
              .map((item) => {
                const id = this.obterIdSeguro(item);
                if (id === null) {
                  return null;
                }

                return {
                  label: this.obterTexto(
                    item,
                    [
                      'nome',
                      'Nome',
                      'razaoSocial',
                      'RazaoSocial',
                      'designacao',
                      'Designacao',
                    ],
                    'Cliente sem designação',
                  ),
                  value: id,
                };
              })
              .filter(
                (item): item is { label: string; value: string | number } =>
                  item !== null,
              ),
          ];

          this.carregandoClientes = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar clientes:', error);
          this.carregandoClientes = false;
        },
      });
  }

  // CARREGAR EFETIVOS
  private carregarEfetivos(): void {
    this.carregandoEfetivos = true;

    this.efetivoService
      .list({
        pageNumber: 1,
        pageSize: 100000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.efetivos = this.extrairDados(response);

          this.efetivosOpcoes = [
            { label: 'Todos os efetivos', value: null },
            ...this.efetivos
              .map((item) => {
                const id = this.obterIdSeguro(item);
                if (id === null) {
                  return null;
                }

                return {
                  label: this.obterTexto(
                    item,
                    [
                      'nome',
                      'Nome',
                      'nomeCompleto',
                      'NomeCompleto',
                      'nomeCompletoEfetivo',
                      'NomeCompletoEfetivo',
                      'fullName',
                    ],
                    'Efetivo sem nome',
                  ),
                  value: id,
                };
              })
              .filter(
                (item): item is { label: string; value: string | number } =>
                  item !== null,
              ),
          ];

          this.construirOpcoesEstadosEfetivo();
          this.carregandoEfetivos = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar efetivos:', error);
          this.carregandoEfetivos = false;
        },
      });
  }

  // ESTADOS DOS EFETIVOS — API
  private construirOpcoesEstadosEfetivo(): void {
    const estadosMap = new Map<number, string>([
      [0, 'ACTIVO'],
      [1, 'INACTIVO'],
      [2, 'AFASTADO'],
      [3, 'SUSPENSO'],
      [4, 'DE FOLGA'],
      [5, 'FÉRIAS'],
    ]);

    const estadosEncontrados = new Set<number>();

    for (const item of this.efetivos) {
      const estado = this.obterValor(item, [
        'estadoEfetivo',
        'EstadoEfetivo',
        'estado',
        'Estado',
      ]);

      if (estado === null || estado === undefined || estado === '') {
        continue;
      }

      const numero = Number(
        typeof estado === 'object'
          ? this.obterValor(estado, ['id', 'Id', 'value', 'Value'])
          : estado,
      );

      if (!Number.isNaN(numero) && estadosMap.has(numero)) {
        estadosEncontrados.add(numero);
      }
    }

    this.estadosEfetivo = [
      { label: 'Todos os Estados', value: null },
      ...Array.from(estadosEncontrados)
        .sort((a, b) => a - b)
        .map((estado) => ({
          label: estadosMap.get(estado)!,
          value: estado,
        })),
    ];
  }

  // CARREGAR EQUIPAMENTOS
  private carregarEquipamentos(): void {
    this.carregandoEquipamentos = true;

    this.equipamentoService
      .list({
        pageNumber: 1,
        pageSize: 10000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.equipamentos = this.extrairDados(response);
          this.construirOpcoesTiposEquipamento();
          this.carregandoEquipamentos = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar equipamentos:', error);
          this.carregandoEquipamentos = false;
        },
      });
  }

  // TIPOS DE EQUIPAMENTO — API
  private construirOpcoesTiposEquipamento(): void {
    this.tiposEquipamentos = this.construirOpcoesDaApi(
      this.equipamentos,
      [
        'tipoEquipamentoId',
        'TipoEquipamentoId',
        'idTipoEquipamento',
        'IdTipoEquipamento',
      ],
      [
        'nomeTipoEquipamento',
        'NomeTipoEquipamento',
        'tipoEquipamentoNome',
        'TipoEquipamentoNome',
        'tipoEquipamento.designacao',
        'tipoEquipamento.Designacao',
        'tipoEquipamento.nome',
        'tipoEquipamento.Nome',
      ],
      'Todos os Equipamentos',
    );
  }

  // CARREGAR CATEGORIAS
  private carregarCategorias(): void {
    this.categoriaService
      .list({
        pageNumber: 1,
        pageSize: 10000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.categorias = this.extrairDados(response);
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar categorias:', error);
        },
      });
  }

  // CARREGAR ALOCAÇÕES
  private carregarAlocacoesEfetivo(): void {
    this.carregandoAlocacoes = true;

    this.contratoEfetivoService
      .getAll({
        pageNumber: 1,
        pageSize: 1000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.alocacoesEfetivo = this.extrairDados(response);
          this.carregandoAlocacoes = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar alocações:', error);
          this.carregandoAlocacoes = false;
        },
      });
  }

  // CARREGAR RONDAS
  private carregarRondas(): void {
    this.carregandoRondas = true;

    this.rondaService
      .list({
        pageNumber: 1,
        pageSize: 10000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.rondasDados = this.extrairDados(response);
          this.construirOpcoesRondas();
          this.carregandoRondas = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar rondas:', error);
          this.carregandoRondas = false;
        },
      });
  }

  // OPÇÕES DE RONDAS — API
  private construirOpcoesRondas(): void {
    this.tiposRonda = this.construirOpcoesDaApi(
      this.rondasDados,
      ['id', 'Id', 'rondaId', 'RondaId'],
      [
        'nomeRonda',
        'NomeRonda',
        'rondaNome',
        'RondaNome',
        'designacao',
        'Designacao',
        'ronda.designacao',
        'ronda.Designacao',
        'ronda.nome',
        'ronda.Nome',
      ],
      'Todas as Rondas',
    );
  }

  // CARREGAR FALTAS
  private carregarFaltas(): void {
    this.carregandoFaltas = true;

    this.faltaService
      .list({
        pageNumber: 1,
        pageSize: 10000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.faltasDados = this.extrairDados(response);
          this.carregandoFaltas = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar faltas:', error);
          this.faltasDados = [];
          this.carregandoFaltas = false;
        },
      });
  }

  // CARREGAR ESCALAS
  private carregarEscalas(): void {
    this.carregandoEscalas = true;

    this.escalaService
      .list({
        pageNumber: 1,
        pageSize: 10000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.escalasDados = this.extrairDados(response);

          this.construirOpcoesEscalas();

          this.carregandoEscalas = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar escalas:', error);
          this.escalasDados = [];
          this.carregandoEscalas = false;
        },
      });
  }

  // CARREGAR TURNOS — usar a lista completa de turnos, sem depender das escalas existentes
  private carregarTurnos(): void {
    this.carregandoTurnos = true;
    this.turnoService
      .getAll({ pageNumber: 1, pageSize: 10000 })
      .subscribe({
        next: (response: unknown) => {
          const turnos = this.extrairDados(response);
          this.turnos = this.construirOpcoesDaApi(
            turnos,
            ['id', 'Id', 'turnoId', 'TurnoId'],
            ['designacao', 'Designacao', 'nome', 'Nome', 'descricao', 'Descricao'],
            'Todos os turnos',
          );
          this.carregandoTurnos = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar turnos:', error);
          this.turnos = [{ label: 'Todos os turnos', value: null }];
          this.carregandoTurnos = false;
        },
      });
  }

  // CARREGAR OCORRÊNCIAS
  private carregarOcorrencias(): void {
    this.carregandoOcorrencias = true;

    this.ocorrenciaService
      .list({
        pageNumber: 1,
        pageSize: 10000,
      })
      .subscribe({
        next: (response: unknown) => {
          this.ocorrenciasDados = this.extrairDados(response);

          this.construirOpcoesCriticidade();
          this.construirOpcoesTipoOcorrencia();

          this.carregandoOcorrencias = false;
        },
        error: (error: unknown) => {
          console.error('Erro ao carregar ocorrências:', error);
          this.ocorrenciasDados = [];
          this.carregandoOcorrencias = false;
        },
      });
  }

  // ESCALAS — API
  private construirOpcoesEscalas(): void {
    this.escalas = this.construirOpcoesDaApi(
      this.escalasDados,
      ['id', 'Id', 'escalaId', 'EscalaId'],
      [
        'nomeEscala',
        'NomeEscala',
        'designacao',
        'Designacao',
        'escalaNome',
        'EscalaNome',
        'escala.designacao',
        'escala.Designacao',
        'escala.nome',
        'escala.Nome',
      ],
      'Todas as escalas',
    );
  }

  // CRITICIDADES — API
  private construirOpcoesCriticidade(): void {
    this.criticidades = this.construirOpcoesDaApi(
      this.ocorrenciasDados,
      [
        'criticidadeEnum',
        'CriticidadeEnum',
        'criticidadeId',
        'CriticidadeId',
        'criticidade.id',
        'criticidade.Id',
      ],
      [
        'criticidade',
        'Criticidade',
        'nomeCriticidade',
        'NomeCriticidade',
        'criticidadeNome',
        'CriticidadeNome',
        'criticidade.designacao',
        'criticidade.Designacao',
        'criticidade.nome',
        'criticidade.Nome',
      ],
      'Todas as Criticidades',
    );
  }

  // TIPOS DE OCORRÊNCIA — API
  private construirOpcoesTipoOcorrencia(): void {
    this.tiposOcorrencia = this.construirOpcoesDaApi(
      this.ocorrenciasDados,
      [
        'tipoOcorrenciaEnum',
        'TipoOcorrenciaEnum',
        'tipoOcorrenciaId',
        'TipoOcorrenciaId',
        'tipoOcorrencia.id',
        'tipoOcorrencia.Id',
      ],
      [
        'tipoOcorrencia',
        'TipoOcorrencia',
        'nomeTipoOcorrencia',
        'NomeTipoOcorrencia',
        'tipoOcorrenciaNome',
        'TipoOcorrenciaNome',
        'tipoOcorrencia.designacao',
        'tipoOcorrencia.Designacao',
        'tipoOcorrencia.nome',
        'tipoOcorrencia.Nome',
      ],
      'Todos os Tipos',
    );
  }

  // PRÉ-VISUALIZAÇÃO
  abrirPreVisualizacao(id: ReportId = this.relatorioSelecionado): void {
    const report = this.obterRelatorio(id);

    if (!report.available) {
      return;
    }

    this.relatorioSelecionado = id;
    this.previewTitulo = report.title;
    this.previewDescricao = report.description;
    this.previewIcone = report.icon;
    this.previewColumns = this.obterColunas(id);
    this.previewRows = this.obterDados(id);

    this.previewVisible = true;
  }

  fecharPreVisualizacao(): void {
    this.previewVisible = false;
  }

  // CONVERTE O VALOR DA API PARA TEXTO
  private obterTextoSeguro(objeto: unknown, caminhos: string[]): string {
    const valor = this.obterValorSeguro(objeto, caminhos);

    if (valor === null || valor === undefined || valor === '') {
      return '—';
    }

    if (typeof valor === 'string') {
      return valor;
    }

    if (typeof valor === 'number' || typeof valor === 'boolean') {
      return String(valor);
    }

    if (typeof valor === 'object') {
      const objetoValor = valor as Record<string, unknown>;

      const propriedadesTexto = [
        'nome',
        'Nome',
        'designacao',
        'Designacao',
        'descricao',
        'Descricao',
        'description',
        'label',
        'Label',
        'valor',
        'Valor',
        'codigo',
        'Codigo',
        'sigla',
        'Sigla',
      ];

      for (const propriedade of propriedadesTexto) {
        const texto = objetoValor[propriedade];

        if (texto !== null && texto !== undefined && texto !== '') {
          return String(texto);
        }
      }
    }

    return '—';
  }

  // OBTÉM VALOR DE UM CAMPO DA API
  private obterValorSeguro(objeto: unknown, caminhos: string[]): unknown {
    if (!objeto || typeof objeto !== 'object') {
      return null;
    }

    for (const caminho of caminhos) {
      const partes = caminho.split('.');
      let valor: unknown = objeto;

      for (const parte of partes) {
        if (valor === null || valor === undefined || typeof valor !== 'object') {
          valor = null;
          break;
        }

        const registro = valor as Record<string, unknown>;

        if (Object.prototype.hasOwnProperty.call(registro, parte)) {
          valor = registro[parte];
        } else {
          valor = null;
          break;
        }
      }

      if (valor !== null && valor !== undefined && valor !== '') {
        return valor;
      }
    }

    return null;
  }

  // DADOS DA PRÉ-VISUALIZAÇÃO
  private obterDados(id: ReportId): PreviewRow[] {
    switch (id) {
      case 'efetivos':
        return this.obterDadosEfetivos();

      case 'faltas':
        return this.obterDadosFaltas();

      case 'escalas':
        return this.obterDadosEscalas();

      case 'ocorrencias':
        return this.obterDadosOcorrencias();

      case 'rondas':
        return this.obterDadosRondas();

      case 'postos':
        return this.obterDadosPostos();

      case 'equipamentos':
        return this.obterDadosEquipamentos();

      default:
        return [];
    }
  }

  // PRÉ-VISUALIZAÇÃO - EFETIVOS
  private obterDadosEfetivos(): PreviewRow[] {
    return this.efetivos
      .filter((item) => this.correspondeAoFiltroEfetivo(item))
      .map((item) => ({
        nome: this.obterTexto(item, ['nome'], '—'),
        posto: this.obterPostoDoEfetivo(item),
        categoria: this.obterCategoriaDoEfetivo(item),
        estado: this.obterEstadoDoObjeto(item),
        admissao: this.formatarData(
          this.obterValor(item, [
            'dataAdmissao',
            'DataAdmissao',
            'admissao',
            'Admissao',
          ]),
        ),
      }));
  }

  // PRÉ-VISUALIZAÇÃO - FALTAS
  private obterDadosFaltas(): PreviewRow[] {
    return this.faltasDados
      .filter((item) => this.correspondeAoFiltroFalta(item))
      .map((item) => ({
        nome: this.obterTexto(
          item,
          [
            'escalaEfetivo.efetivoNome',
            'efetivoNome',
            'EfetivoNome',
            'efetivo.nome',
            'efetivo.Nome',
          ],
          '—',
        ),
        posto: this.obterTexto(
          item,
          [
            'escala.postoNome',
            'postoNome',
            'PostoNome',
            'posto.nome',
            'posto.Nome',
          ],
          '—',
        ),
        estado: this.obterTexto(
          item,
          ['estado', 'Estado', 'justificacao', 'Justificacao'],
          '—',
        ),
        data: this.formatarData(
          this.obterValor(item, [
            'dataFaltaInicio',
            'DataFaltaInicio',
            'dataInicio',
            'DataInicio',
          ]),
        ),
        tipo: this.obterTexto(
          item,
          ['tipoFalta', 'TipoFalta', 'tipoFaltaNome', 'TipoFaltaNome'],
          '—',
        ),
      }));
  }

  // PRÉ-VISUALIZAÇÃO - ESCALAS
  private obterDadosEscalas(): PreviewRow[] {
    return this.escalasDados
      .filter((item) => this.correspondeAoFiltroEscala(item))
      .map((item) => ({
        posto: this.obterTextoSeguro(item, ['nomePosto']),
        turno: this.obterTextoSeguro(item, ['nomeTurno']),
        inicio: this.formatarDataHora(this.obterValorSeguro(item, ['dataInicio'])),
        termino: this.formatarDataHora(this.obterValorSeguro(item, ['dataFim'])),
        estado: this.obterTextoSeguro(item, [
          'estado.nome',
          'estado.Nome',
          'estado.designacao',
          'estado.Designacao',
          'estado.descricao',
          'estado.Descricao',
          'estadoEscala.nome',
          'estadoEscala.Nome',
          'estadoEscala.designacao',
          'estadoEscala.Designacao',
          'estadoEscala.descricao',
          'estadoEscala.Descricao',
          'estadoEscala',
          'EstadoEscala',
          'nomeEstado',
          'NomeEstado',
          'estadoNome',
          'EstadoNome',
          'estado',
          'Estado',
          'escala.estado.nome',
          'escala.estado.Nome',
          'escala.estado.designacao',
          'escala.estado.Designacao',
          'escala.estadoEscala',
          'escala.EstadoEscala',
        ]),
      }));
  }

  // PRÉ-VISUALIZAÇÃO - OCORRÊNCIAS
  private obterDadosOcorrencias(): PreviewRow[] {
    return this.ocorrenciasDados
      .filter((item) => this.correspondeAoFiltroOcorrencia(item))
      .map((item) => ({
        data: this.formatarDataHora(this.obterValor(item, ['dataOcorrencia'])),
        criticidade: this.obterTexto(item, ['criticidade'], '—'),
        tipo: this.obterTexto(item, ['tipoOcorrencia'], '—'),
        posto: this.obterTexto(item, ['nomePosto'], '—'),
        efetivo: this.obterTexto(item, ['nomeEfetivo'], '—'),
      }));
  }

  // PRÉ-VISUALIZAÇÃO - RONDAS
  private obterDadosRondas(): PreviewRow[] {
    return this.rondasDados
      .filter((item) => this.correspondeAoFiltroRonda(item))
      .map((item) => ({
        data: this.formatarDataHora(this.obterValor(item, ['dataInicio'])),
        posto: this.obterTexto(item, ['nomePosto'], '—'),
        efetivo: this.obterTexto(item, ['nomeEfetivo'], '—'),
        estado: this.obterEstadoRonda(item),
      }));
  }

  // PRÉ-VISUALIZAÇÃO - POSTOS
  private obterDadosPostos(): PreviewRow[] {
    return this.postosDados
      .filter((item) => this.correspondeAoFiltroPosto(item))
      .map((item) => ({
        codigo: this.obterTexto(item, ['codigo'], '—'),
        nome: this.obterTexto(item, ['nome'], '—'),
        cliente: this.obterTexto(item, ['nomeCliente'], '—'),
        municipio: this.obterTexto(item, ['nomeMunicipio'], '—'),
        provincia: this.obterTexto(item, ['nomeProvincia'], '—'),
        estado: this.obterEstadoDoObjeto(item['estado']),
      }));
  }

  // PRÉ-VISUALIZAÇÃO - EQUIPAMENTOS
  private obterDadosEquipamentos(): PreviewRow[] {
    return this.equipamentos
      .filter((item) => this.correspondeAoFiltroEquipamento(item))
      .map((item) => ({
        codigo: this.obterTexto(
          item,
          ['codigoInterno', 'CodigoInterno', 'codigo', 'Codigo'],
          '—',
        ),
        tipo: this.obterTexto(
          item,
          [
            'nomeTipoEquipamento',
            'NomeTipoEquipamento',
            'tipoEquipamento.designacao',
            'tipoEquipamento.Designacao',
            'tipoEquipamento.nome',
            'tipoEquipamento.Nome',
          ],
          '—',
        ),
        marca: this.obterTexto(item, ['marca', 'Marca'], '—'),
        modelo: this.obterTexto(item, ['modelo', 'Modelo'], '—'),
        posto: this.obterTexto(
          item,
          [
            'posto.nome',
            'posto.Nome',
            'posto.designacao',
            'posto.Designacao',
            'nomePosto',
            'NomePosto',
          ],
          '—',
        ),
        estado: this.obterEstadoDoObjeto(item),
      }));
  }

  // FILTRO - EFETIVOS
  private correspondeAoFiltroEfetivo(item: any): boolean {
    const postoId = this.obterValor(item, [
      'idPosto',
      'IdPosto',
      'postoId',
      'PostoId',
      'posto.id',
      'posto.Id',
    ]);

    const clienteId = this.obterValor(item, [
      'idCliente',
      'IdCliente',
      'clienteId',
      'ClienteId',
      'cliente.id',
      'cliente.Id',
    ]);

    const estado = this.obterValor(item, [
      'estadoEfetivo',
      'EstadoEfetivo',
      'estado',
      'Estado',
    ]);

    const dataAdmissao = this.obterValor(item, [
      'dataAdmissao',
      'DataAdmissao',
      'admissao',
      'Admissao',
    ]);
    const dataAdmissaoISO = this.normalizarDataISO(dataAdmissao);

    return (
      this.correspondeId(this.postoSelecionado, postoId) &&
      this.correspondeId(this.clienteSelecionado, clienteId) &&
      this.correspondeValor(this.estadoSelecionado, estado) &&
      (!this.dataAdmissao || dataAdmissaoISO === this.dataAdmissao)
    );
  }

  private normalizarDataISO(valor: unknown): string | null {
    if (!valor) return null;
    const texto = String(valor);
    const dataISO = texto.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (dataISO) return dataISO;

    const data = new Date(texto);
    if (Number.isNaN(data.getTime())) return null;
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  // FILTRO - FALTAS
  private correspondeAoFiltroFalta(item: any): boolean {
    const postoId = this.obterValor(item, [
      'postoId',
      'PostoId',
      'escala.postoId',
      'escala.PostoId',
    ]);

    const clienteId = this.obterValor(item, [
      'clienteId',
      'ClienteId',
      'escala.clienteId',
      'escala.ClienteId',
    ]);

    return (
      this.correspondeId(this.postoSelecionado, postoId) &&
      this.correspondeId(this.clienteSelecionado, clienteId)
    );
  }

  // FILTRO - ESCALAS
  private correspondeAoFiltroEscala(item: any): boolean {
    const postoId = this.obterValor(item, [
      'postoId',
      'PostoId',
      'escala.postoId',
      'escala.PostoId',
      'posto.id',
      'posto.Id',
    ]);

    const turnoId = this.obterValor(item, [
      'turnoId',
      'TurnoId',
      'escala.turnoId',
      'escala.TurnoId',
      'turno.id',
      'turno.Id',
    ]);

    const estado = this.obterValor(item, [
      'estadoEscalaEnum',
      'EstadoEscalaEnum',
      'estado',
      'Estado',
      'escala.estadoEscalaEnum',
      'escala.EstadoEscalaEnum',
    ]);

    const escalaId = this.obterValor(item, [
      'escalaId',
      'EscalaId',
      'id',
      'Id',
      'escala.id',
      'escala.Id',
    ]);

    return (
      this.correspondeId(this.postoSelecionado, postoId) &&
      this.correspondeId(this.turnoSelecionado, turnoId) &&
      this.correspondeValor(this.estadoSelecionado, estado) &&
      this.correspondeId(this.escalaSelecionada, escalaId)
    );
  }

  // FILTRO - RONDAS
  private correspondeAoFiltroRonda(item: any): boolean {
    const postoId = this.obterValor(item, ['idPosto']);

    const efetivoId = this.obterValor(item, [
      'idEfetivo',
      'IdEfetivo',
      'efetivoId',
      'EfetivoId',
      'efetivo.id',
      'efetivo.Id',
    ]);

    const rondaId = this.obterValor(item, ['id', 'Id', 'rondaId', 'RondaId']);

    const concluida = this.obterValor(item, ['concluida', 'Concluida']);

    return (
      this.correspondeId(this.postoSelecionado, postoId) &&
      this.correspondeId(this.efetivoSelecionado, efetivoId) &&
      this.correspondeId(this.rondaSelecionada, rondaId) &&
      (!this.SomenteEmcurso || concluida === false || concluida === 'false')
    );
  }

  // FILTRO - POSTOS
  private correspondeAoFiltroPosto(item: any): boolean {
    if (this.postoSelecionado === null) {
      return true;
    }

    return this.correspondeId(this.postoSelecionado, this.obterIdSeguro(item));
  }

  // FILTRO - EQUIPAMENTOS
  private correspondeAoFiltroEquipamento(item: any): boolean {
    const postoId = this.obterValor(item, [
      'idPosto',
      'IdPosto',
      'postoId',
      'PostoId',
      'posto.id',
      'posto.Id',
    ]);

    const dataAquisicao = this.normalizarDataISO(this.obterValor(item, [
      'dataAquisicao',
      'DataAquisicao',
    ]));

    return (
      this.correspondeId(this.postoSelecionado, postoId) &&
      (!this.dataAquisicao || dataAquisicao === this.dataAquisicao)
    );
  }

  // FILTRO - OCORRÊNCIAS
  private correspondeAoFiltroOcorrencia(item: any): boolean {
    const postoId = this.obterValor(item, [
      'postoId',
      'PostoId',
      'idPosto',
      'IdPosto',
      'posto.id',
      'posto.Id',
    ]);

    const efetivoId = this.obterValor(item, [
      'efetivoId',
      'EfetivoId',
      'idEfetivo',
      'IdEfetivo',
      'efetivo.id',
      'efetivo.Id',
    ]);

    const rondaId = this.obterValor(item, [
      'rondaId',
      'RondaId',
      'idRonda',
      'IdRonda',
      'ronda.id',
      'ronda.Id',
    ]);

    const criticidade = this.obterValor(item, [
      'criticidadeEnum',
      'CriticidadeEnum',
      'criticidadeId',
      'CriticidadeId',
      'criticidade.id',
      'criticidade.Id',
    ]);

    const tipoOcorrencia = this.obterValor(item, ['tipoOcorrenciaEnum']);

    return (
      this.correspondeId(this.postoSelecionado, postoId) &&
      this.correspondeId(this.efetivoSelecionado, efetivoId) &&
      this.correspondeId(this.rondaSelecionada, rondaId) &&
      this.correspondeValor(this.criticidadeSelecionada, criticidade) &&
      this.correspondeValor(this.tipoOcorrenciaSelecionado, tipoOcorrencia)
    );
  }

  // COLUNAS DA PRÉ-VISUALIZAÇÃO
  private obterColunas(id: ReportId): PreviewColumn[] {
    switch (id) {
      case 'efetivos':
        return [
          { key: 'nome', label: 'NOME' },
          { key: 'categoria', label: 'CATEGORIA' },
          { key: 'admissao', label: 'ADMISSÃO' },
        ];

      case 'faltas':
        return [
          { key: 'nome', label: 'NOME' },
          { key: 'posto', label: 'POSTO' },
          { key: 'data', label: 'DATA' },
        ];

      case 'escalas':
        return [
          { key: 'turno', label: 'TURNO' },
          { key: 'inicio', label: 'INÍCIO' },
          { key: 'termino', label: 'TÉRMINO' },
        ];

      case 'ocorrencias':
        return [
          { key: 'data', label: 'DATA' },
          { key: 'criticidade', label: 'CRITICIDADE' },
          { key: 'tipo', label: 'TIPO' },
        ];

      case 'rondas':
        return [
          { key: 'data', label: 'DATA' },
          { key: 'posto', label: 'POSTO' },
          { key: 'efetivo', label: 'EFETIVO' },
          { key: 'estado', label: 'ESTADO' },
        ];

      case 'postos':
        return [
          { key: 'codigo', label: 'CÓDIGO' },
          { key: 'nome', label: 'POSTO' },
          { key: 'cliente', label: 'CLIENTE' },
          { key: 'municipio', label: 'MUNICÍPIO' },
          { key: 'provincia', label: 'PROVÍNCIA' },
        ];

      case 'equipamentos':
        return [
          { key: 'codigo', label: 'CÓDIGO' },
          { key: 'tipo', label: 'TIPO' },
          { key: 'estado', label: 'ESTADO' },
        ];

      default:
        return [];
    }
  }

  // POSTO DO EFETIVO
  private obterPostoDoEfetivo(item: any): string {
    const postoDireto = this.obterTexto(
      item,
      [
        'posto.nome',
        'posto.Nome',
        'posto.designacao',
        'posto.Designacao',
      ],
      '',
    );

    if (postoDireto) {
      return postoDireto;
    }

    const postoId = this.obterValor(item, [
      'idPosto',
      'IdPosto',
      'postoId',
      'PostoId',
    ]);

    if (postoId === null || postoId === undefined) {
      return '—';
    }

    const posto = this.postosDados.find(
      (itemPosto) =>
        String(this.obterIdSeguro(itemPosto)) === String(postoId),
    );

    return this.obterTexto(
      posto,
      ['nome', 'Nome', 'designacao', 'Designacao', 'nomePosto', 'NomePosto'],
      '—',
    );
  }

  // CATEGORIA
  private obterCategoriaDoEfetivo(item: any): string {
    const categoriaDireta = this.obterTexto(
      item,
      [
        'categoria.designacao',
        'categoria.Designacao',
        'categoria.nome',
        'categoria.Nome',
      ],
      '',
    );

    if (categoriaDireta) {
      return categoriaDireta;
    }

    const categoriaId = this.obterValor(item, [
      'idCategoria',
      'IdCategoria',
      'categoriaId',
      'CategoriaId',
    ]);

    if (categoriaId === null || categoriaId === undefined) {
      return '—';
    }

    const categoria = this.categorias.find(
      (itemCategoria) =>
        String(this.obterIdSeguro(itemCategoria)) === String(categoriaId),
    );

    return this.obterTexto(
      categoria,
      ['designacao', 'Designacao', 'nome', 'Nome'],
      '—',
    );
  }

  // ESTADO DE QUALQUER OBJETO
  private obterEstadoDoObjeto(item: any): string {
    if (!item) {
      return '—';
    }

    const valor = this.obterValor(item, [
      'estadoNome',
      'EstadoNome',
      'nomeEstado',
      'NomeEstado',
      'estadoDescricao',
      'EstadoDescricao',
      'estado',
      'Estado',
      'status',
      'Status',
    ]);

    if (valor === null || valor === undefined || valor === '') {
      return '—';
    }

    return String(valor);
  }

  // ESTADO DA RONDA
  private obterEstadoRonda(item: any): string {
    const concluida = this.obterValor(item, ['concluida', 'Concluida']);

    if (concluida === true || concluida === 'true') {
      return 'Concluída';
    }

    if (concluida === false || concluida === 'false') {
      return 'Em curso';
    }

    return this.obterEstadoDoObjeto(item);
  }

  // OPÇÕES DINÂMICAS A PARTIR DA API
  private construirOpcoesDaApi(
    dados: any[],
    camposValor: string[],
    camposLabel: string[],
    labelTodos: string,
  ): RelatorioSelectOption[] {
    const mapa = new Map<string, RelatorioSelectOption>();

    for (const item of dados) {
      const valor = this.obterValor(item, camposValor);
      const label = this.obterTexto(item, camposLabel, '');

      if (valor === null || valor === undefined || valor === '' || !label) {
        continue;
      }

      const chave = String(valor);

      if (!mapa.has(chave)) {
        mapa.set(chave, {
          label,
          value: valor,
        });
      }
    }

    return [{ label: labelTodos, value: null }, ...Array.from(mapa.values())];
  }

  // OBTÉM VALOR DE PROPRIEDADE
  private obterValor(item: any, caminhos: string[]): any {
    if (!item) {
      return undefined;
    }

    for (const caminho of caminhos) {
      const partes = caminho.split('.');
      let atual = item;
      let encontrou = true;

      for (const parte of partes) {
        if (atual === null || atual === undefined) {
          encontrou = false;
          break;
        }

        atual = atual[parte];
      }

      if (encontrou && atual !== null && atual !== undefined && atual !== '') {
        return atual;
      }
    }

    return undefined;
  }

  // OBTÉM TEXTO
  private obterTexto(
    item: any,
    caminhos: string[],
    valorPadrao = '—',
  ): string {
    const valor = this.obterValor(item, caminhos);

    if (valor === null || valor === undefined || valor === '') {
      return valorPadrao;
    }

    if (typeof valor === 'object') {
      const texto =
        valor.nome ??
        valor.Nome ??
        valor.designacao ??
        valor.Designacao ??
        valor.descricao ??
        valor.Descricao;

      return texto ? String(texto) : valorPadrao;
    }

    return String(valor);
  }

  // COMPARAÇÃO DE IDs
  private correspondeId(
    selecionado: string | number | null,
    valor: any,
  ): boolean {
    if (selecionado === null || selecionado === undefined || selecionado === '') {
      return true;
    }

    if (valor === null || valor === undefined || valor === '') {
      return false;
    }

    return String(valor) === String(selecionado);
  }

  // COMPARAÇÃO DE VALORES
  private correspondeValor(
    selecionado: string | number | null,
    valor: any,
  ): boolean {
    if (selecionado === null || selecionado === undefined || selecionado === '') {
      return true;
    }

    if (valor === null || valor === undefined || valor === '') {
      return false;
    }

    return String(valor) === String(selecionado);
  }

  // DATA
  private formatarData(valor: any): string {
    if (!valor) {
      return '—';
    }

    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return String(valor);
    }

    return data.toLocaleDateString('pt-AO');
  }

  // DATA E HORA
  private formatarDataHora(valor: any): string {
    if (!valor) {
      return '—';
    }

    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return String(valor);
    }

    return data.toLocaleString('pt-AO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // NORMALIZAR NOME
  private normalizarNome(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .toLowerCase();
  }

  // EXTRAIR DADOS
  private extrairDados(response: unknown): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (response && typeof response === 'object') {
      const data = response as Record<string, unknown>;

      const propriedades = [
        'data',
        'records',
        'items',
        'results',
        'content',
        'value',
      ];

      for (const propriedade of propriedades) {
        const valor = data[propriedade];

        if (Array.isArray(valor)) {
          return valor;
        }
      }
    }

    return [];
  }

  // ID
  private obterIdSeguro(item: any): string | number | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const id =
      item.id ??
      item.Id ??
      item.ID ??
      item.codigoInterno ??
      item.CodigoInterno;

    if (id === undefined || id === null || id === '') {
      return null;
    }

    if (typeof id !== 'string' && typeof id !== 'number') {
      return null;
    }

    return id;
  }

  // NÚMERO OPCIONAL
  private obterNumeroOpcional(
    valor: string | number | null,
  ): number | undefined {
    if (valor === null || valor === undefined || valor === '') {
      return undefined;
    }

    const numero = Number(valor);

    return Number.isNaN(numero) ? undefined : numero;
  }
}
